import { paymentBiometricAssertionType } from '@/services/models/api.models';

const toBuffer = (value: string): ArrayBuffer => {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  return Uint8Array.from(atob(padded), (char) => char.charCodeAt(0)).buffer;
};

const toBase64Url = (buffer: ArrayBuffer): string =>
  btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

export const isWebAuthnSupported = (): boolean =>
  typeof window !== 'undefined' &&
  !!window.PublicKeyCredential &&
  !!navigator.credentials &&
  window.isSecureContext;

async function requestCredential(optionsJson: string): Promise<PublicKeyCredential> {
  const options = JSON.parse(optionsJson);

  const credential = (await navigator.credentials.get({
    publicKey: {
      challenge: toBuffer(options.challenge),
      rpId: options.rpId,
      timeout: options.timeout,
      userVerification: options.userVerification,
      allowCredentials: (options.allowCredentials ?? []).map(
        (item: { id: string; type: PublicKeyCredentialType; transports?: AuthenticatorTransport[] }) => ({
          id: toBuffer(item.id),
          type: item.type,
          transports: item.transports,
        }),
      ),
    },
  })) as PublicKeyCredential | null;

  if (!credential) throw new Error('No credential returned');

  return credential;
}

const DEVICE_MARKER_COOKIE = 'ozone_bio_devices';

const readRememberedIds = (): string[] => {
  const entry = document.cookie
    .split('; ')
    .filter((item) => item.indexOf(DEVICE_MARKER_COOKIE + '=') === 0)[0];
  return entry ? entry.substring(DEVICE_MARKER_COOKIE.length + 1).split(',') : [];
};

export const isRememberedBiometricDevice = (optionsJson: string): boolean => {
  const allowed: string[] = (JSON.parse(optionsJson).allowCredentials ?? []).map((item: { id: string }) => item.id);
  const remembered = readRememberedIds();
  return allowed.some((id) => remembered.indexOf(id) !== -1);
};

const rememberBiometricDevice = (credentialId: string, domain?: string) => {
  if (!credentialId || !domain) return;
  const ids = readRememberedIds().filter((id) => id && id !== credentialId);
  ids.unshift(credentialId);
  document.cookie =
    DEVICE_MARKER_COOKIE + '=' + ids.slice(0, 5).join(',') +
    '; Domain=' + domain + '; Path=/; Max-Age=31536000; SameSite=Lax; Secure';
};

export async function proveDeviceBiometric(optionsJson: string): Promise<void> {
  const credential = await requestCredential(optionsJson);
  rememberBiometricDevice(credential.id, JSON.parse(optionsJson).rpId);
}

export async function getPaymentAssertion(optionsJson: string): Promise<paymentBiometricAssertionType> {
  const credential = await requestCredential(optionsJson);
  const response = credential.response as AuthenticatorAssertionResponse;

  return {
    id: credential.id,
    rawId: toBase64Url(credential.rawId),
    type: credential.type,
    response: {
      authenticatorData: toBase64Url(response.authenticatorData),
      clientDataJSON: toBase64Url(response.clientDataJSON),
      signature: toBase64Url(response.signature),
      userHandle: response.userHandle ? toBase64Url(response.userHandle) : null,
    },
  };
}
